import { Document } from 'dashboard/models/document';
import {
  DocumentViewerActions,
  DocumentViewerActionType,
} from 'dashboard/redux/actions/document-viewer-base';

export interface DocumentViewerState {
  document: Document;
  rotation: number;
  zoomScale: number;
  zoomVisualValue: string;
}

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const getInitialState: () => DocumentViewerState = () => {
  return {
    document: undefined,
    rotation: 0,
    zoomScale: 1.0,
    zoomVisualValue: '100%',
  };
};

export const initialState: DocumentViewerState = getInitialState();

// returns the index of the closest value that is larger or equal to the search value
function binarySearchIndex(
  arr: number[],
  leftIndex: number,
  rightIndex: number,
  searchValue: number,
): number {
  const midIndex = leftIndex + Math.floor((rightIndex - leftIndex) / 2);
  const midValue = arr[midIndex];
  if (midValue === searchValue) {
    return midIndex;
  } else if (midValue > searchValue) {
    if (midIndex - 1 < leftIndex) {
      return midIndex;
    }
    return binarySearchIndex(arr, leftIndex, midIndex - 1, searchValue);
  } else {
    if (rightIndex < midIndex + 1) {
      return midIndex + 1;
    }
    return binarySearchIndex(arr, midIndex + 1, rightIndex, searchValue);
  }
}

const zoomPresets = [
  0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.25, 1.5, 1.75, 2.0, 2.5, 3.0, 4.0, 5.0,
];

function getUpdatedStateWithNewZoomScale(state: DocumentViewerState, newZoomScale: number) {
  const validNewZoomScale = Math.min(
    zoomPresets[zoomPresets.length - 1],
    Math.max(zoomPresets[0], newZoomScale),
  );
  return {
    ...state,
    zoomScale: validNewZoomScale,
    // floating points have a weird issue where 0.55 * 100 becomes 55.000000001
    zoomVisualValue: `${Math.round(validNewZoomScale * 100)}%`,
  };
}

const documentViewerReducer = (
  state: DocumentViewerState = initialState,
  action: DocumentViewerActions,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
): DocumentViewerState => {
  switch (action.type) {
    case DocumentViewerActionType.ResetBaseState: {
      return getInitialState();
    }
    case DocumentViewerActionType.SetDocument: {
      return {
        ...state,
        document: action.payload.document,
      };
    }
    case DocumentViewerActionType.RotateDocument: {
      const newRotation = (state.rotation + 90) % 360;
      return {
        ...state,
        rotation: newRotation,
      };
    }
    case DocumentViewerActionType.SetZoomScale: {
      return getUpdatedStateWithNewZoomScale(state, action.payload.zoomScale);
    }
    case DocumentViewerActionType.IncrementZoomScale: {
      let closestLargerPresetIndex = binarySearchIndex(
        zoomPresets,
        0,
        zoomPresets.length,
        state.zoomScale,
      );
      if (zoomPresets[closestLargerPresetIndex] === state.zoomScale) {
        closestLargerPresetIndex += 1;
      }
      if (closestLargerPresetIndex < zoomPresets.length) {
        const newZoomScale = zoomPresets[closestLargerPresetIndex];
        if (newZoomScale !== state.zoomScale) {
          return getUpdatedStateWithNewZoomScale(state, newZoomScale);
        }
      }
      break;
    }
    case DocumentViewerActionType.DecrementZoomScale: {
      const closestSmallerPresetIndex =
        binarySearchIndex(zoomPresets, 0, zoomPresets.length, state.zoomScale) - 1;
      if (closestSmallerPresetIndex >= 0) {
        const newZoomScale = zoomPresets[closestSmallerPresetIndex];
        return getUpdatedStateWithNewZoomScale(state, newZoomScale);
      }
      break;
    }
    case DocumentViewerActionType.SetZoomVisualValue: {
      return {
        ...state,
        zoomVisualValue: action.payload.zoomVisualValue,
      };
    }

    default:
      return state;
  }
};

export default documentViewerReducer;
